import React, { useEffect, useState } from "react";
import SearchableSelect from "../../../../core/common/SearchableSelect";
import Select from "react-select";
import Swal from "sweetalert2";
import useApi from "../../../../core/customhook/useApi";
import {
  CustomMultilineOption,
  CustomPackageSingleValue,
  packageSelectStyles,
} from "./CustomMultilineOption";
import useProjects from "../../../../core/customhook/useProjects";

interface AssignPackageProps {
  show: boolean;
  handleClose: () => void;
  onSuccess?: () => void;
}

const AssignPackage: React.FC<AssignPackageProps> = ({
  show,
  handleClose,
  onSuccess,
}) => {
  const { projects, loading } = useProjects();

  const [filterData, setFilterData] = useState({
    account_name: "",
    program: "",
    dept_loc: "",
    package: "",
  });

  const [programList, setProgramList] = useState([]);
  const [departmentList, setDepartmentList] = useState([]);
  const [packageList, setPackageList] = useState([]);

  const getPrograms = useApi<any>("/api/v1/master/projects", {
    method: "GET",
    autoCall: false,
    handleGlobalError: false,
  });

  const getDepartments = useApi<any>("", {
    method: "GET",
    autoCall: false,
    handleGlobalError: false,
  });

  const getPackages = useApi<any>("/api/v1/admin/packages", {
    method: "GET",
    autoCall: false,
    handleGlobalError: false,
  });

  const attachPackageToDepartment = useApi<any>(
    "/api/v1/admin/packages/assign/departments",
    {
      method: "POST",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const res = await getPackages.callApi();

        if (res?.success) {
          setPackageList(res.data);
        }
      } catch {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Unable to load packages.",
        });
      }
    };

    fetchPackages();
  }, []);

  // const handleProjectChange = async (projectId: string) => {
  //   setFilterData((prev) => ({
  //     ...prev,
  //     account_name: projectId,
  //     program: "",
  //     dept_loc: "",
  //   }));

  //   setProgramList([]);
  //   setDepartmentList([]);
  //   onSuccess?.();

  //   try {
  //     const res = await getPrograms.callApi(
  //       {},
  //       `/api/v1/master/projects/${projectId}/programs`,
  //     );

  //     if (res?.success) {
  //       setProgramList(res.data);
  //     }
  //   } catch {
  //     Swal.fire({
  //       icon: "error",
  //       title: "Error",
  //       text: "Unable to load programs.",
  //     });
  //   }
  // };
  const handleProjectChange = async (projectId: string) => {
    setFilterData((prev) => ({
      ...prev,
      account_name: projectId,
      program: "",
      dept_loc: "",
    }));

    setProgramList([]);
    setDepartmentList([]);

    try {
      const res = await getPrograms.callApi(
        {},
        `/api/v1/master/projects/${projectId}/programs`,
      );

      if (res?.success) {
        setProgramList(res.data);
      }
    } catch {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Unable to load programs.",
      });
    }
  };
  const handleProgramChange = async (programId: string) => {
    setFilterData((prev) => ({
      ...prev,
      program: programId,
      dept_loc: "",
    }));

    setDepartmentList([]);

    try {
      const res = await getDepartments.callApi(
        {},
        `/api/v1/master/programs/${programId}/departments`,
      );

      if (res?.success) {
        setDepartmentList(res.data);
      }
    } catch {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Unable to load departments.",
      });
    }
  };

  const handleFieldDataChange = (name: string, value: string | number) => {
    if (name === "account_name") {
      handleProjectChange(value as string);
      return;
    }

    if (name === "program") {
      handleProgramChange(value as string);
      return;
    }

    setFilterData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const packageOptions = packageList.map((item: any) => ({
    projectId: item.package_id,
    label: item.package_name,
    name: item.package_name,
    code: item.package_code,
    scope: item.sop,
  }));

  const handleSave = async () => {
    // Basic validation
    if (
      !filterData.account_name ||
      !filterData.program ||
      !filterData.dept_loc ||
      !filterData.package
    ) {
      Swal.fire({
        icon: "warning",
        title: "Required Fields",
        text: "Please complete all required fields before assigning the package.",
      });
      return;
    }
    try {
      // Show loading
      Swal.fire({
        title: "Assigning Package",
        text: "Please wait while the package is being assigned...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const departmentPayload = {
        packageId: filterData.package,
        departmentId: filterData.dept_loc,
        projectId: filterData.account_name,
        programId: filterData.program,
      };

      const departmentRes =
        await attachPackageToDepartment.callApi(departmentPayload);

      if (!departmentRes?.success) {
        throw new Error(
          departmentRes?.error ||
            departmentRes?.message ||
            "Unable to assign package to department.",
        );
      }

      Swal.fire({
        icon: "success",
        title: "Package Assigned",
        text: "The package has been successfully assigned to the department.",
        timer: 1800,
        showConfirmButton: true,
      });

      // Reload parent table
      await onSuccess?.();

      // Reset form
      setFilterData({
        account_name: "",
        program: "",
        dept_loc: "",
        package: "",
      });

      setProgramList([]);
      setDepartmentList([]);

      handleClose();
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: "Assignment Failed",
        text:
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          error?.message ||
          "Something went wrong while assigning the package.",
      });
    }
  };

  if (!show) return null;

  return (
    <div
      className="modal fade show d-block"
      tabIndex={-1}
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div
        className="modal-dialog modal-xl modal-dialog-centered "
        style={{ minWidth: "450px", maxWidth: "700px" }}
      >
        <div className="modal-content border-0 rounded-3">
          {/* Header */}
          <div className="modal-header">
            <h4 className="fw-bold">Assign package</h4>

            <button
              type="button"
              className="btn-close"
              onClick={handleClose}
            ></button>
          </div>

          {/* Body */}
          <div className="modal-body">
            <div className="row gx-3">
              {/* Client Name */}
              <div className="col-md-6">
                <SearchableSelect
                  label="Brand Name"
                  name="account_name"
                  value={filterData.account_name}
                  options={projects.map((item: any) => ({
                    label: item.project_name,
                    value: item.project_id,
                  }))}
                  onChange={handleFieldDataChange}
                  required
                />
              </div>

              {/* Program */}
              <div className="col-md-6">
                <SearchableSelect
                  label="Program Name"
                  name="program"
                  value={filterData.program}
                  options={programList.map((item: any) => ({
                    label: item.program_name,
                    value: item.program_id,
                  }))}
                  onChange={handleFieldDataChange}
                  disabled={!filterData.account_name}
                  required
                />
              </div>

              {/* Dept/LOC */}
              <div className="col-md-12">
                <SearchableSelect
                  label="Department"
                  name="dept_loc"
                  value={filterData.dept_loc}
                  options={departmentList.map((item: any) => ({
                    label: item.department_name,
                    value: item.department_id,
                  }))}
                  onChange={handleFieldDataChange}
                  disabled={!filterData.program}
                  required
                />
              </div>

              {/* Package */}
              {/* Package — multi-line custom select */}
              <div className="col-md-12">
                <label
                  style={{
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "#333",
                    marginBottom: 6,
                    display: "block",
                  }}
                >
                  Package <sup className="text-danger">*</sup>
                </label>
                <Select
                  className="mb-1 border-0"
                  options={packageOptions}
                  projectId={
                    packageOptions.find(
                      (item) => item.projectId === filterData.package,
                    ) || null
                  }
                  onChange={(option: any) =>
                    handleFieldDataChange("package", option?.projectId)
                  }
                  components={{
                    Option: CustomMultilineOption,
                    SingleValue: CustomPackageSingleValue,
                  }}
                  styles={packageSelectStyles}
                  placeholder="Search or select a package..."
                  isClearable
                  isSearchable
                />
                {/* {errors?.package && <ErrorMessage message={errors.package} />} */}
              </div>
              {/* ====================== */}
            </div>
          </div>

          {/* Footer Button */}
          <div className="modal-footer ">
            <button type="button" className="btn border" onClick={handleClose}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-danger px-4 ms-2"
              // onClick={handleClose}
              onClick={handleSave}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignPackage;
